/**
 * Líneas de producto expuestas por GET /v1/private/productlines (BFF → ms-application).
 * Solo deben usarse en UI las marcadas como habilitadas (`enabled !== false`).
 */
export type SelectableProductLine = {
  code: string;
  label: string;
  enabled?: boolean;
};

export const UNAVAILABLE_PRODUCT_LINE_LABEL = 'Producto no disponible';

const PRODUCT_LINE_SELECT_PLACEHOLDER = 'Seleccionar producto';

export function parseSelectableProductLines(data: unknown): SelectableProductLine[] {
  if (!Array.isArray(data)) return [];
  return data
    .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
    .map((row) => ({
      code: String(row.code ?? '').trim(),
      label: String(row.label ?? '').trim(),
      enabled: typeof row.enabled === 'boolean' ? row.enabled : undefined,
    }))
    .filter((row) => row.code.length > 0 && row.label.length > 0)
    .filter((row) => row.enabled !== false);
}

export function defaultSelectableProductLineCode(lines: SelectableProductLine[]): string {
  return lines[0]?.code ?? '';
}

export function resolveSelectableProductLineLabel(
  lines: SelectableProductLine[],
  code: string
): string | undefined {
  if (!code) return undefined;
  return lines.find((p) => p.code === code)?.label;
}

/** Texto del combo cuando el código guardado no está entre las líneas habilitadas. */
export function productLineDropdownTitle(
  lines: SelectableProductLine[],
  productLineCode: string | undefined
): string {
  if (!productLineCode) return PRODUCT_LINE_SELECT_PLACEHOLDER;
  const label = resolveSelectableProductLineLabel(lines, productLineCode);
  return label ?? PRODUCT_LINE_SELECT_PLACEHOLDER;
}

export function isSelectableProductLineCode(lines: SelectableProductLine[], code: string): boolean {
  if (!code) return false;
  return lines.some((p) => p.code === code);
}

/**
 * Catalog `application.product_lines.code` values are always 3 characters
 * (`001`..`008`, `100`..`102`). G2 navigation puts numeric `productId` in the
 * same route slot — those must not hit the legacy grouped BFF matcher by code.
 */
export function isLegacyProductLineCodeParam(param: string): boolean {
  return /^\d{3}$/.test(param);
}

/**
 * Seed id → code for standard Movil / Flow Cap product_lines rows.
 * Not a pad of the id (id 9 → `100`, not `009`).
 */
const PRODUCT_LINE_CODE_BY_ID: Record<number, string> = {
  1: '001',
  2: '002',
  3: '003',
  4: '004',
  5: '005',
  6: '006',
  7: '007',
  8: '008',
  9: '100',
  10: '101',
  11: '102',
};

export function resolveProductLineCodeFromId(productId: number): string | undefined {
  return PRODUCT_LINE_CODE_BY_ID[productId];
}

export function resolveProductLineCode(routeParam: string, productId: number): string {
  if (isLegacyProductLineCodeParam(routeParam)) return routeParam;
  return resolveProductLineCodeFromId(productId) ?? routeParam;
}
